// =============================================================================
// Product pricing — tier table refresh + tier-aware Each / Total calculation.
//
// Two responsibilities:
//   1. Size <select> change → AJAX POST to data-pricing-url, swap the response
//      HTML into .js-pricingTable, then recompute totals.
//   2. Qty change (.js-totalTrigger, product page only) → look up the matching
//      tier locally, highlight it, write Each + Total into .js-eachPrice /
//      .js-totalPrice.
//
// Tier markup contract — each tier element carries data-min, optional data-max
// (omit for ∞), and data-price:
//
//   Vertical (sub-category thumb):
//     <tr data-min="1" data-max="9" data-price="96.99">…</tr>
//
//   Horizontal (product page):
//     <tr><th>Quantity:</th>
//         <td data-min="1"  data-max="10" data-price="11.33">1-10</td>
//         <td data-min="11" data-max="50" data-price="10.25">11-50</td>
//     </tr>
//     <tr><th>Price per each:</th><td>$11.33</td>…</tr>
//
// On the horizontal layout, `.is-current` is also applied to the same-column
// cell in any sibling row of the matched tier's tbody so both rows highlight.
// =============================================================================
(function ($) {
    "use strict";

    const DEBOUNCE_MS = 350;

    function parseInfinity(v) {
        if (v === undefined || v === null || v === "") {
            return Infinity;
        }
        const n = parseInt(v, 10);
        return Number.isFinite(n) ? n : Infinity;
    }

    // Tier matching qty. Falls back to nearest tier when out of range so the
    // total never drops to 0.
    function findMatchingTier($table, qty) {
        const tiers = $table.find("[data-min][data-price]").toArray();
        if (!tiers.length) {
            return null;
        }

        for (let i = 0; i < tiers.length; i++) {
            const min = parseInt(tiers[i].dataset.min, 10);
            const max = parseInfinity(tiers[i].dataset.max);
            if (qty >= min && qty <= max) {
                return tiers[i];
            }
        }

        const sorted = tiers
            .slice()
            .sort(
                (a, b) =>
                    parseInt(a.dataset.min, 10) - parseInt(b.dataset.min, 10),
            );
        if (qty < parseInt(sorted[0].dataset.min, 10)) {
            return sorted[0];
        }
        return sorted[sorted.length - 1];
    }

    function highlightTier($table, matchedEl) {
        $table.find(".is-current").removeClass("is-current");
        if (!matchedEl) {
            return;
        }

        const $matched = $(matchedEl).addClass("is-current");

        // Horizontal layout: highlight same-column cells across sibling rows.
        const $row = $matched.closest("tr");
        if ($row.length && matchedEl.tagName === "TD") {
            const idx = $row.children().index($matched);
            if (idx >= 0) {
                $row.parent()
                    .children("tr")
                    .each(function () {
                        const sibling = this.children[idx];
                        if (sibling && sibling !== matchedEl) {
                            sibling.classList.add("is-current");
                        }
                    });
            }
        }
    }

    function formatMoney(n) {
        if (!Number.isFinite(n)) {
            return "0.00";
        }
        return n.toFixed(2);
    }

    function updateTotals($form) {
        const $table = $form.find(".js-pricingTable");
        if (!$table.length) {
            return;
        }

        const $each = $form.find(".js-eachPrice");
        const $total = $form.find(".js-totalPrice");

        const qty = parseInt($form.find('input[name="qty"]').val(), 10) || 0;
        const matched = findMatchingTier($table, qty);
        highlightTier($table, matched);

        if (!$each.length && !$total.length) {
            return;
        }
        if (!matched) {
            $each.text("0.00");
            $total.text("0.00");
            return;
        }

        const unit = parseFloat(matched.dataset.price) || 0;
        $each.text(formatMoney(unit));
        $total.text(formatMoney(unit * qty));
    }

    function refreshPricing($form) {
        const url = $form.attr("data-pricing-url");
        if (!url) {
            return;
        }

        const $table = $form.find(".js-pricingTable");
        if (!$table.length) {
            return;
        }

        const inflight = $form.data("pricingXhr");
        if (inflight) {
            inflight.abort();
        }

        $table.addClass("is-loading");

        const xhr = $.ajax({
            method: $form.attr("method") || "POST",
            url,
            data: $form.serialize(),
            dataType: "html",
        });

        $form.data("pricingXhr", xhr);

        xhr.done((html) => {
            const $current = $form.find(".js-pricingTable");
            if (!$current.length) {
                return;
            }

            const $incoming = $("<div>").html(html).children().first();
            if ($incoming.is(".js-pricingTable")) {
                $current.replaceWith($incoming);
            } else {
                $current.html(html).removeClass("is-loading");
            }

            updateTotals($form);
        }).fail((jqXhr, textStatus) => {
            if (textStatus !== "abort") {
                $form.find(".js-pricingTable").removeClass("is-loading");
                if (window.notyf) {
                    window.notyf.error(
                        "Something went wrong. Please try again.",
                    );
                }
            }
        });
    }

    $(function () {
        $(document).on(
            "input change",
            ".js-pricingForm .js-pricingTrigger",
            function () {
                const $form = $(this).closest(".js-pricingForm");
                if (!$form.length) {
                    return;
                }

                clearTimeout($form.data("pricingTimer"));
                $form.data(
                    "pricingTimer",
                    setTimeout(() => refreshPricing($form), DEBOUNCE_MS),
                );
            },
        );

        // Qty change recomputes totals only — sub-category qty inputs don't
        // carry .js-totalTrigger, so they stay silent.
        $(document).on(
            "input change",
            ".js-pricingForm .js-totalTrigger",
            function () {
                const $form = $(this).closest(".js-pricingForm");
                if ($form.length) {
                    updateTotals($form);
                }
            },
        );

        $(".js-pricingForm").each(function () {
            updateTotals($(this));
        });
    });
})(jQuery);
